import {useMediaQuery} from '@mui/material'
import Slide from '@mui/material/Slide'
import SwipeableDrawer from '@mui/material/SwipeableDrawer'
import Lottie from 'lottie-react'
import {useCallback, useEffect, useMemo, useState} from 'react'
import {useDispatch} from 'react-redux'
import {useLocation, useNavigate} from 'react-router-dom'
import {
  deleteStackNotification,
  NeutralNotificationTypes,
  NotificationType,
  NotificationTypes,
  useTopNotification,
  useTopUpNotification
} from 'entities/activity'
import {Header, Text, TextButton} from 'entities/activity/config'
import {ContentLastMessage, DialogItemType} from 'entities/messages'
import {closeGallery} from 'entities/profile'
import {useSound} from 'entities/user'
import {mediaVariants} from 'shared/lib'
import {playAudio} from 'shared/lib/sound'
import {Avatar} from 'shared/ui/avatar'
import {Button} from 'shared/ui/buttons'
import {Cross, Hot, Verified} from 'shared/ui/icons'
import bellAnimation from './img/bell-animation.json'
import styles from './styles.module.scss'
import './drawer.scss'

const closeMessageSecond = 5
const timeCloseNotification = Number(styles.timeCloseNotification) // 10

let timer: number

const excludeUrls = [
  'terms-of-use',
  'privacy-policy',
  'community-rules',
  'dating-safety',
  'anti-trafficking-policy',
  'crypto-pay',
  'threeDs'
]

export const Notification = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const notification = useTopNotification()
  const upNotification = useTopUpNotification()
  const is768p = useMediaQuery(`(max-width: ${styles.is768p})`)
  const [isOpen, setIsOpen] = useState(true)

  useEffect(() => {
    if (!location.pathname.includes('messages')) return

    notification?.type === 'dialog' && dispatch(deleteStackNotification())
    upNotification?.type === 'dialog' && dispatch(deleteStackNotification(1))

    return () => {
      notification?.type === 'dialog' && clearTimeout(timer)
    }
  }, [location.pathname, notification, upNotification])

  useEffect(() => {
    setIsOpen(!excludeUrls.some((item) => location.pathname.includes(item)))
  }, [location.pathname, setIsOpen])

  if (!notification || !isOpen) return <></>

  if (is768p) return <MobileNotificationBlock />

  if (notification.type === 'notification') {
    return <NotificationBlock />
  }

  return <MessageBlock />
}

const NotificationBlock = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const {item: notification} = useTopNotification() as {item: NotificationType}
  const [isAnimate, setIsAnimate] = useState(false)
  const isSound = useSound()

  const openClickHandler = useCallback(() => {
    if (!notification) return

    onCloseHandler()
    if (notification.type === NotificationTypes.LIKE || notification.type === NotificationTypes.FAVORITE) {
      return navigate(`/profile/${notification.from.id}`)
    }

    navigate(`/messages/${notification.from.id}`)
  }, [navigate, notification])

  const onCloseHandler = useCallback(() => {
    setIsAnimate(false)
    const timer = setTimeout(() => dispatch(deleteStackNotification()), 300)

    return () => {
      clearTimeout(timer)
    }
  }, [dispatch, deleteStackNotification])

  useEffect(() => {
    setIsAnimate(false)
    if (!notification) return

    clearTimeout(timer)
    timer = window.setTimeout(onCloseHandler, timeCloseNotification * 1000)

    setTimeout(() => {
      setIsAnimate(true)
      isSound && playAudio()
    }, 300)

    return () => {
      clearTimeout(timer)
    }
  }, [notification])

  return (
    <div className={`${styles.notification} ${isAnimate ? styles.open : ''}`}>
      <div className={styles.header}>
        <Lottie animationData={bellAnimation} className={styles.bell} /> {Header[notification.type]}
      </div>
      <div className={styles.userInfo}>
        <Avatar
          src={notification.from.avatarUrl + mediaVariants.square64}
          alt={notification.from.name}
          isOnline={notification.from.isOnline}
          sx={{width: 48, height: 48}}
        />
        <div className={styles.info}>
          <div className={styles.name}>
            <span>{notification.from.name}, {notification.from.age}</span>
            {notification.from.isConfirmed ? <div className={styles.verifiedIcon}><Verified /></div> : null}
            {notification.from.isPopular ? <div className={styles.hotIcon}><Hot /> </div> : null}
          </div>
          <div className={styles.text}>
            <span>{Text[notification.type]?.replace('%Name%', notification.from.name)}</span>
          </div>
        </div>
      </div>
      <div className={styles.buttons}>
        <Button styleType='outlinedGray' onClick={onCloseHandler}>Dismiss</Button>
        <Button styleType='primary' onClick={openClickHandler} className={styles.chat}>
          <div className={`${styles.back} ${isAnimate ? styles.animate : ''}`} />
          <span>{TextButton[notification.type]}</span>
        </Button>
      </div>
    </div>
  )
}

const MessageBlock = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const {item: dialog} = useTopNotification() as {item: DialogItemType}
  const upNotification = useTopUpNotification()
  const [isAnimate, setIsAnimate] = useState(false)
  const isSound = useSound()

  useEffect(() => {
    if (!upNotification || upNotification.type !== 'dialog') return

    const timer = setTimeout(() => onCloseHandler(), 1000)

    return () => {
      clearTimeout(timer)
    }
  }, [upNotification])

  const openChat = useCallback(() => {
    if (!dialog) return

    onCloseHandler()
    navigate(`/messages/${dialog.interlocutor.id}`)
  }, [navigate, dialog])

  const onCloseHandler = useCallback(() => {
    setIsAnimate(false)
    const timer = setTimeout(() => dispatch(deleteStackNotification()), 300)

    return () => {
      clearTimeout(timer)
    }
  }, [dispatch, deleteStackNotification])

  useEffect(() => {
    setIsAnimate(false)
    if (!dialog) return

    clearTimeout(timer)
    timer = window.setTimeout(onCloseHandler, closeMessageSecond * 1000)

    setTimeout(() => {
      setIsAnimate(true)
      isSound && playAudio()
    }, 300)

    return () => {
      clearTimeout(timer)
    }
  }, [dialog])

  return (
    <div className={`${styles.notification} ${isAnimate ? styles.open : ''} ${styles.dialog}`} onClick={openChat}>
      <div>
        <div className={styles.crossIcon} onClick={(e) => {e.stopPropagation(); onCloseHandler()}}>
          <Cross />
        </div>
        <div className={styles.userInfo}>
          <Avatar
            src={dialog.interlocutor.avatarUrl + mediaVariants.square64}
            alt={dialog.interlocutor.name}
            isOnline={dialog.interlocutor.isOnline}
            sx={{width: 48, height: 48}}
          />
          <div className={styles.info}>
            <div className={styles.name}>
              <span>{dialog.interlocutor.name}, {dialog.interlocutor.age}</span>
              {dialog.interlocutor.isConfirmed ? <div className={styles.verifiedIcon}><Verified /></div> : null}
              {dialog.interlocutor.isPopular ? <div className={styles.hotIcon}><Hot /></div> : null}
            </div>
            <div className={styles.text}><ContentLastMessage dialog={dialog} /></div>
          </div>
        </div>
      </div>
    </div>
  )
}

const MobileNotificationBlock = () => {
  const [open, setOpen] = useState(false)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const {type, item: topNotification} = useTopNotification()
  const isSound = useSound()
  const [isAnimate, setIsAnimate] = useState(false)

  const openClickHandler = useCallback(() => {
    if (!topNotification) return

    dispatch(closeGallery())
    onCloseHandler()

    if (type === 'notification') {
      const notification = topNotification as NotificationType

      if (
        notification.type === NotificationTypes.LIKE ||
        notification.type === NotificationTypes.FAVORITE ||
        notification.type === NeutralNotificationTypes.PHOTO_UPLOADED
      ) {
        return navigate(`/profile/${notification.from.id}`)
      }

      return navigate(`/messages/${notification.from.id}`)
    }

    const dialog = topNotification as DialogItemType
    navigate(`/messages/${dialog.interlocutor.id}`)
  }, [navigate, topNotification])

  const onCloseHandler = useCallback(() => {
    setOpen(false)
    const timer = setTimeout(() => dispatch(deleteStackNotification()), 300)

    return () => {
      clearTimeout(timer)
    }
  }, [dispatch, deleteStackNotification])

  const onTouchStartHandler = () => {
    clearTimeout(timer)
    setIsAnimate(false)
  }

  const onTouchEndHandler = () => {
    timer = window.setTimeout(onCloseHandler, timeCloseNotification * 1000)
    setIsAnimate(true)
  }

  useEffect(() => {
    setIsAnimate(false)
    if (!topNotification) return

    setOpen(true)
    clearTimeout(timer)
    timer = window.setTimeout(onCloseHandler, timeCloseNotification * 1000)
    isSound && playAudio()
    setIsAnimate(true)

    return () => {
      clearTimeout(timer)
    }
  }, [topNotification])

  const interlocutor = type === 'dialog'
    ? (topNotification as DialogItemType).interlocutor
    : (topNotification as NotificationType).from

  const text = type === 'dialog'
    ? <ContentLastMessage dialog={topNotification as DialogItemType} />
    : Header[(topNotification as NotificationType).type]

  const textButton = useMemo(() => type === 'dialog'
    ? 'Reply'
    : (topNotification as NotificationType).type === NeutralNotificationTypes.PHOTO_UPLOADED ? 'Visit profile' : 'Chat now',
  [type])

  return (
    <SwipeableDrawer
      classes={{root: 'mobileNotificationRoot'}}
      anchor='top'
      open={open}
      onClose={onCloseHandler}
      onOpen={() => {}}
      disableEnforceFocus
      disableAutoFocus
      disableSwipeToOpen={false}
    >
      <Slide
        direction='down'
        in={open}
        mountOnEnter
        unmountOnExit
        onTouchStart={onTouchStartHandler}
        onTouchEnd={onTouchEndHandler}
      >
        <div className={styles.mobileNotification} onClick={openClickHandler}>
          <Avatar
            src={interlocutor.avatarUrl + mediaVariants.square64}
            alt={interlocutor.name}
            isOnline={interlocutor.isOnline}
            sx={{width: 52, height: 52}}
          />
          <div className={styles.info}>
            <div className={styles.name}>
              <span>{interlocutor.name}, {interlocutor.age}</span>
              {interlocutor.isConfirmed ? <div className={styles.verifiedIcon}><Verified /></div> : null}
              {interlocutor.isPopular ? <div className={styles.hotIcon}><Hot /></div> : null}
            </div>
            <div className={styles.text}><span>{text}</span></div>
          </div>
          {type === 'notification'
            ? <Button styleType='primary' className={styles.replyButton} onClick={openClickHandler}>
              <div className={`${styles.back} ${isAnimate ? styles.animate : ''}`} />
              <span>{textButton}</span>
            </Button>
            : null}
        </div>
      </Slide>
    </SwipeableDrawer>
  )
}
